import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@heroui/react';
import { ConfigData, DisplayAt, Route, TimerType } from '@/types';
import { useConfigSettings } from '@/hooks/useConfigSettings';
import { DEFAULT_CONFIG } from '@lib/defaultConfig';
import { buildCreateModeConfig, cloneConfigBase } from '@lib/createModeConfig';
import { normalizeConfigUrlForInteractivity } from '@lib/normalizeConfigUrlForInteractivity';
import { useSession } from '@context/sessionProvider';
import { useRouter } from 'next/router';
import { applyTimerTemplate } from '@lib/timerTemplates';
import { getDefaultCountdownPosition } from '@lib/countdownPosition';
import { normalizeConfigData } from '@lib/appearanceConfig';
import { PRODUCT_CONTAINER_PRODUCT_TARGET_ENABLED } from '@lib/timerTypeGates';
import { buildDefaultCornerRadius } from '@lib/cornerRadius';

interface SettingsContextProps {
	settings: ConfigData;
	localConfigData: ConfigData;
	configId: number | undefined;
	updateSettings: (updatedConfig: Partial<ConfigData>) => void;
	saveSettings: () => Promise<void>;
	isLoading: boolean;
	isEditMode: boolean;
}

interface SettingsProviderProps {
	tid: string | undefined;
	children: ReactNode;
	onLoad?: () => void;
	onError?: (error: string) => void;
}

export const SettingsContext = createContext<SettingsContextProps | undefined>(undefined);

export function useSettings(): SettingsContextProps {
	const context = useContext(SettingsContext);
	if (!context) {
		throw new Error('useSettings must be used within a SettingsProvider');
	}

	return context;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ tid, children, onLoad, onError }) => {
	const { user } = useSession();
	const router = useRouter();
	const [localConfigData, setLocalConfigData] = useState<ConfigData>(() => cloneConfigBase());
	const initializedTemplateKeyRef = useRef<string | null>(null);
	const dirtyCreateTemplateKeyRef = useRef<string | null>(null);

	const [isSaving, setIsSaving] = useState(false);
	const [activeTid, setActiveTid] = useState<number | undefined>(() =>
		tid && !Number.isNaN(Number(tid)) ? Number.parseInt(tid, 10) : undefined,
	);

	const mode = activeTid ? Route.EDIT : Route.CREATE;
	const isEditMode = mode === Route.EDIT;
	const isCreateMode = !isEditMode;
	const isCreatePage = router.isReady && router.pathname === '/create';
	const templateId = getQueryValue(router.query.template);
	const countdownPosition = getQueryValue(router.query.countdownPosition);
	const queryTimerType = getQueryValue(router.query.timerType);
	const createTimerType = resolveCreateTimerType(queryTimerType);
	const createCountdownPosition = countdownPosition ?? getDefaultCountdownPosition(createTimerType);
	const createTemplateKey = `${templateId ?? 'standard'}:${createCountdownPosition}:${createTimerType}:${user.timezoneName ?? ''}`;

	const { configData, isLoading, error, saveSettings: saveSettingsToServer } = useConfigSettings(activeTid);
	const normalizedConfigData = useMemo(() => (configData ? normalizeConfigData(configData) : undefined), [configData]);

	useEffect(() => {
		const parsedTid = tid && !Number.isNaN(Number(tid)) ? Number.parseInt(tid, 10) : undefined;
		setActiveTid(parsedTid);
	}, [tid]);

	useEffect(() => {
		if (!isCreatePage) {
			initializedTemplateKeyRef.current = null;
			dirtyCreateTemplateKeyRef.current = null;
			return;
		}

		if (
			!isCreateMode ||
			dirtyCreateTemplateKeyRef.current === createTemplateKey ||
			initializedTemplateKeyRef.current === createTemplateKey
		) {
			return;
		}

		const createConfig = buildCreateModeConfig(user.timezoneName);
		const timerTypeConfig = applyCreateTimerType(createConfig, createTimerType);
		setLocalConfigData(applyTimerTemplate(timerTypeConfig, templateId, createCountdownPosition));
		initializedTemplateKeyRef.current = createTemplateKey;
		dirtyCreateTemplateKeyRef.current = null;
	}, [
		isCreatePage,
		isCreateMode,
		createTemplateKey,
		templateId,
		createCountdownPosition,
		createTimerType,
		user.timezoneName,
	]);

	useEffect(() => {
		if (isEditMode && normalizedConfigData) {
			setLocalConfigData(normalizedConfigData);
		}
	}, [isEditMode, normalizedConfigData]);

	useEffect(() => {
		if (isEditMode && error && onError) {
			onError('Failed to load settings. Please try again.');
		}
	}, [onError, isEditMode, error]);

	useEffect(() => {
		if (!onLoad) {
			return;
		}

		if (isCreateMode) {
			onLoad();

			return;
		}

		if (isEditMode && !isLoading) {
			if (error) {
				return;
			}
			onLoad();
		}
	}, [onLoad, isCreateMode, isEditMode, isLoading, error]);

	const getChangedFields = (original: ConfigData, updated: ConfigData): Partial<ConfigData> => {
		return Object.fromEntries(
			Object.entries(updated).filter(([key, value]) => {
				const typedKey = key as keyof ConfigData;

				return !areConfigValuesEqual(typedKey, original[typedKey], value);
			}),
		) as Partial<ConfigData>;
	};

	const updateSettings = (updatedConfig: Partial<ConfigData>) => {
		if (isCreatePage && isCreateMode && initializedTemplateKeyRef.current) {
			dirtyCreateTemplateKeyRef.current = initializedTemplateKeyRef.current;
		}

		setLocalConfigData((prev) => {
			const filteredConfig = Object.fromEntries(
				Object.entries(updatedConfig).filter(([, value]) => value !== undefined),
			) as Partial<ConfigData>;

			return { ...prev, ...filteredConfig };
		});
	};

	const saveSettings = async () => {
		setIsSaving(true);
		try {
			if (isEditMode) {
				if (!activeTid) {
					throw new Error('Timer ID is missing.');
				}

				const normalizedLocalConfigData = normalizeConfigData(localConfigData);
				let updatedConfig = normalizedConfigData
					? getChangedFields(normalizedConfigData, normalizedLocalConfigData)
					: normalizedLocalConfigData;
				updatedConfig = normalizeConfigUrlForInteractivity(updatedConfig, normalizedLocalConfigData.interactivity);
				if (Object.keys(updatedConfig).length === 0) {
					toast.success('Settings', { description: 'Settings saved successfully!' });

					return;
				}

				await saveSettingsToServer(updatedConfig, activeTid);

				return;
			}

			const normalizedLocalConfigData = normalizeConfigData(localConfigData);
			const createPayload = normalizeConfigUrlForInteractivity(
				normalizedLocalConfigData,
				normalizedLocalConfigData.interactivity,
			);
			const { id } = await saveSettingsToServer(createPayload);
			if (id) {
				setActiveTid(id);
				await router.replace({ pathname: '/edit/[tid]', query: { tid: id } });
			}
		} catch (saveError) {
			toast.danger('Settings', {
				description: (saveError as Error).message ?? 'An unexpected error occurred.',
			});
		} finally {
			setIsSaving(false);
		}
	};

	const settings = isEditMode && normalizedConfigData ? normalizedConfigData : DEFAULT_CONFIG;

	return (
		<SettingsContext.Provider
			value={{
				settings,
				localConfigData,
				configId: activeTid,
				updateSettings,
				saveSettings,
				isLoading: isSaving,
				isEditMode,
			}}
		>
			{children}
		</SettingsContext.Provider>
	);
};

function getQueryValue(value: string | string[] | undefined): string | undefined {
	return Array.isArray(value) ? value[0] : value;
}

function resolveCreateTimerType(value: string | undefined): TimerType {
	if (value === TimerType.PRODUCT_CONTAINER && PRODUCT_CONTAINER_PRODUCT_TARGET_ENABLED) {
		return TimerType.PRODUCT_CONTAINER;
	}

	return TimerType.BAR;
}

function applyCreateTimerType(config: ConfigData, timerType: TimerType): ConfigData {
	if (timerType !== TimerType.PRODUCT_CONTAINER) {
		return {
			...config,
			...buildDefaultCornerRadius(TimerType.BAR),
			timer_type: TimerType.BAR,
			display_at: DisplayAt.HOME,
		};
	}

	return {
		...config,
		...buildDefaultCornerRadius(TimerType.PRODUCT_CONTAINER),
		timer_type: TimerType.PRODUCT_CONTAINER,
		display_at: DisplayAt.PRODUCT,
		specificPages: [],
		categories: [],
		products: [],
	};
}

function areConfigValuesEqual(key: keyof ConfigData, original: unknown, updated: unknown): boolean {
	if (isRelationField(key)) {
		return (
			stableSerialize(canonicalizeRelationValue(key, original)) ===
			stableSerialize(canonicalizeRelationValue(key, updated))
		);
	}

	if (Array.isArray(original) || Array.isArray(updated) || isPlainObject(original) || isPlainObject(updated)) {
		return stableSerialize(original) === stableSerialize(updated);
	}

	return original === updated;
}

function isRelationField(key: keyof ConfigData): key is 'products' | 'categories' | 'specificPages' {
	return key === 'products' || key === 'categories' || key === 'specificPages';
}

function canonicalizeRelationValue(key: 'products' | 'categories' | 'specificPages', value: unknown): unknown[] {
	if (!Array.isArray(value)) {
		return [];
	}

	if (key === 'specificPages') {
		return value
			.filter(isPlainObject)
			.map((page) => ({ url: typeof page.url === 'string' ? page.url : '' }))
			.filter((page) => page.url.length > 0)
			.sort((left, right) => left.url.localeCompare(right.url));
	}

	return value
		.filter(isPlainObject)
		.map((item) => {
			const externalId = typeof item.externalId === 'number' && Number.isInteger(item.externalId) ? item.externalId : 0;

			if (key === 'categories') {
				return {
					externalId,
					name: typeof item.name === 'string' ? item.name : '',
					categoryUrl: typeof item.categoryUrl === 'string' ? item.categoryUrl : '',
				};
			}

			return {
				externalId,
				name: typeof item.name === 'string' ? item.name : '',
				productUrl: typeof item.productUrl === 'string' ? item.productUrl : null,
				imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : null,
			};
		})
		.filter((item) => item.externalId > 0)
		.sort((left, right) => left.externalId - right.externalId);
}

function stableSerialize(value: unknown): string {
	return JSON.stringify(sortSerializableValue(value));
}

function sortSerializableValue(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(sortSerializableValue);
	}

	if (!isPlainObject(value)) {
		return value;
	}

	return Object.keys(value)
		.sort()
		.reduce<Record<string, unknown>>((sorted, key) => {
			sorted[key] = sortSerializableValue(value[key]);

			return sorted;
		}, {});
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}
